// @ts-nocheck
/*
 * Port of views/dashboard/dashboard-ivr-list.js.
 * API: API_DASHBOARD_IVR_LISTING (single GET, ?startDate=&endDate=&status=pending).
 * 180-day rolling window, 60s auto-refresh + countdown, IST timezone.
 * Column count preserved: 4 (IVR Name, Status, Last Updated, Created At).
 * Uses obd3-gui's DataTable — same table system as the rest of the app.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Clock, RefreshCw } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall } from '@/service/httpCall';
import { API_DASHBOARD_IVR_LISTING } from '@/service/url';
import { Card } from '@/components/ui/card';
import { DataTable } from '@/components/data-table/DataTable';

const MAX_LISTING_DAYS = 180;
const AUTO_REFRESH_SECONDS = 60;

const normalizeRows = (resp) => {
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.rows)) return resp.rows;
  return [];
};

const startOfDay = (value) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const d = startOfDay(value);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const safeDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const formatDateForCell = (value) => {
  const date = safeDate(value);
  if (!date) return { date: '--', time: '' };
  return {
    date: date.toLocaleDateString('en-GB', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }),
    time: date.toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }),
  };
};

function StatusPill() {
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold text-white"
      style={{ background: '#c45800' }}
    >
      <span className="leading-none">●</span>
      Pending
    </span>
  );
}

function DateTimeCell({ parts }) {
  return (
    <div>
      <div className="font-semibold text-foreground">{parts.date}</div>
      {parts.time ? <div className="mt-0.5 text-xs text-muted-foreground">{parts.time}</div> : null}
    </div>
  );
}

export default function DashboardIvrList() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [refreshCountdownSec, setRefreshCountdownSec] = useState(AUTO_REFRESH_SECONDS);

  const loadIvrListing = useCallback((showLoader = false) => {
    const today = startOfDay(new Date());
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - (MAX_LISTING_DAYS - 1));
    const query = `?startDate=${encodeURIComponent(
      toDateInputValue(startDate),
    )}&endDate=${encodeURIComponent(toDateInputValue(today))}&status=pending`;
    if (showLoader) setLoading(true);
    return getApiCall(`${API_DASHBOARD_IVR_LISTING}${query}`)
      .then((resp) => setRows(normalizeRows(resp)))
      .catch(() => setRows([]))
      .finally(() => { if (showLoader) setLoading(false); });
  }, []);

  useEffect(() => {
    loadIvrListing(true);
    const refreshInterval = setInterval(() => {
      if (!document.hidden) loadIvrListing(false); // skip in background tabs
      setRefreshCountdownSec(AUTO_REFRESH_SECONDS);
    }, AUTO_REFRESH_SECONDS * 1000);
    const countdownInterval = setInterval(() => {
      setRefreshCountdownSec((prev) => (prev <= 1 ? AUTO_REFRESH_SECONDS : prev - 1));
    }, 1000);
    return () => {
      clearInterval(refreshInterval);
      clearInterval(countdownInterval);
    };
  }, [loadIvrListing]);

  const tableRows = useMemo(
    () =>
      (Array.isArray(rows) ? rows : []).map((r, idx) => ({
        key: r?.id || idx + 1,
        id: r?.id,
        flow_name: r?.flow_name || '--',
        status: Number(r?.status),
        updated_at_parts: formatDateForCell(r?.updated_at),
        created_at_parts: formatDateForCell(r?.created_at),
      })),
    [rows],
  );

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      { accessorKey: 'flow_name', header: 'IVR Name' },
      {
        id: 'status',
        header: 'Status',
        cell: () => <StatusPill />,
      },
      {
        id: 'updated_at',
        header: 'Last Updated',
        cell: ({ row }) => (
          <div className="inline-flex items-center gap-2">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            <DateTimeCell parts={row.original.updated_at_parts} />
          </div>
        ),
      },
      {
        id: 'created_at',
        header: 'Created At',
        cell: ({ row }) => <DateTimeCell parts={row.original.created_at_parts} />,
      },
    ],
    [],
  );

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-bold text-foreground">
          <span>Pending IVRs</span>
          {tableRows.length > 0 && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
              {tableRows.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            loadIvrListing(false);
            setRefreshCountdownSec(AUTO_REFRESH_SECONDS);
          }}
          className="inline-flex h-8 items-center gap-1.5 rounded-full border border-border bg-card px-3 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          title="Refresh Pending IVRs"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {refreshCountdownSec}s
        </button>
      </div>

      <DataTable
        tableId="dashboard.ivr-list"
        columns={columns}
        data={tableRows}
        isLoading={loading}
        searchPlaceholder="Search IVR name…"
        emptyTitle="No pending IVRs"
        emptyDescription="Nothing pending in the last 180 days."
        pageSize={10}
        exportFilename="pending-ivrs.csv"
      />
    </>
  );
}
